import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'dn_theme';

function readSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readSavedTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light' || v === 'auto') return v;
  } catch (_) {}
  return 'dark';
}

function applyTheme(setting) {
  const resolved = setting === 'auto' ? readSystemTheme() : setting;
  const html = document.documentElement;
  if (resolved === 'dark') html.classList.add('dark');
  else html.classList.remove('dark');
  html.setAttribute('data-theme', resolved);
}

function applyDensity(density) {
  // Pin to either 'compact' or 'comfortable' so CSS can target it.
  const value = density === 'compact' ? 'compact' : 'comfortable';
  document.documentElement.setAttribute('data-density', value);
}

export function ThemeProvider({ children }) {
  const [setting, setSetting] = useState(readSavedTheme); // dark | light | auto
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('dn_density') || 'comfortable'; } catch (_) { return 'comfortable'; }
  });

  // Apply on mount & when changed
  useEffect(() => { applyTheme(setting); }, [setting]);
  useEffect(() => { applyDensity(density); }, [density]);

  // Listen to system theme changes when auto
  useEffect(() => {
    if (setting !== 'auto') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('auto');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [setting]);

  // Sync with backend on login — ONLY if no local preference exists yet
  // AND user is authenticated. localStorage is the source of truth for theme
  // so users don't see a flicker when the DB differs from local. Skipping the
  // API call when there's no token prevents an infinite reload loop on public
  // routes (/v/{token}, /sign/{token}, /login) where the user isn't signed in.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return; // not authenticated — don't trigger 401-then-refresh-then-redirect loop
        const hasLocalTheme = !!localStorage.getItem(STORAGE_KEY);
        const hasLocalDensity = !!localStorage.getItem('dn_density');
        if (hasLocalTheme && hasLocalDensity) return; // nothing to sync from DB
        const { data } = await api.get('/settings/me');
        if (cancel) return;
        const prefs = data?.preferences || {};
        if (!hasLocalTheme && prefs.theme && prefs.theme !== setting) {
          setSetting(prefs.theme);
          try { localStorage.setItem(STORAGE_KEY, prefs.theme); } catch (_) {}
        }
        if (!hasLocalDensity && prefs.density && prefs.density !== density) {
          setDensity(prefs.density);
          try { localStorage.setItem('dn_density', prefs.density); } catch (_) {}
        }
      } catch (_) { /* not logged in or first load — ignore */ }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTheme = useCallback(async (next) => {
    setSetting(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
    try { await api.put('/settings/me/preferences', { theme: next }); } catch (_) {}
  }, []);

  const updateDensity = useCallback(async (next) => {
    setDensity(next);
    try { localStorage.setItem('dn_density', next); } catch (_) {}
    try { await api.put('/settings/me/preferences', { density: next }); } catch (_) {}
  }, []);

  const resolved = setting === 'auto' ? readSystemTheme() : setting;

  return (
    <ThemeContext.Provider value={{ setting, resolved, density, setTheme: updateTheme, setDensity: updateDensity }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
